import { User } from 'lucide-react';
import type { PatientInfo } from '@refdash/api-client';
import { getPatientName } from '@/lib/utils';

interface PatientInfoCardProps {
  info: PatientInfo;
  reference: string;
  gmsReferralId?: number | string | null;
}

const fields: { key: string; label: string }[] = [
  { key: 'phone', label: 'Mobile' },
  { key: 'identity_type', label: 'Identity Type' },
  { key: 'national_id', label: 'National ID' },
  { key: 'nationality', label: 'Nationality' },
  { key: 'payment_method', label: 'Payment Method' },
  { key: 'required_specialty', label: 'Specialty' },
  { key: 'provider', label: 'Provider' },
  { key: 'referral_cause', label: 'Referral Cause' },
  { key: 'bed_type', label: 'Bed Type' },
  { key: 'source_provider', label: 'Source Provider' },
  { key: 'ref_type', label: 'Ref Type' },
];

export function PatientInfoCard({ info, reference, gmsReferralId }: PatientInfoCardProps) {
  const name = getPatientName(info);

  const visibleFields = fields.filter(
    (f) => info[f.key] !== undefined && info[f.key] !== null && info[f.key] !== '',
  );

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-border">
        <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center shrink-0">
          <User className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-sans font-bold text-foreground truncate">{name}</p>
          <div className="flex items-center gap-3 mt-0.5">
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Ihalaty</p>
              <p className="text-xs text-muted-foreground font-mono">{reference}</p>
            </div>
            {gmsReferralId && (
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">GMS</p>
                <p className="text-xs text-blue-600 font-mono">{gmsReferralId}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Details — always visible */}
      <div className="px-4 py-3">
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          {visibleFields.map((field) => (
            <div key={field.key} className={field.key === 'referral_cause' ? 'col-span-2' : ''}>
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">
                {field.label}
              </p>
              <p className="text-sm text-foreground font-medium">
                {field.key === 'payment_method'
                  ? ({ cash: 'Cash (self-pay)', insurance: 'Insurance' }[String(info[field.key])] ?? String(info[field.key]))
                  : String(info[field.key])}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
